import { type ChangeEvent, useEffect, useState } from "react";

import { SearchInput } from "@vega-core/components/Form/Controls/Input/SearchInput/SearchInput";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";

import {
  type FilterState,
  FilterType,
  type FilterValue,
  type SearchFilterConfig,
  type TableFilterId,
} from "../types";
import { getFilterValue } from "../utils/getFilterValue";

// search debounce delay should be short in tests
const SEARCH_DEBOUNCE_DELAY_MS = process.env.NODE_ENV === "test" ? 10 : 300;

type Props = {
  filter: SearchFilterConfig;
  isLoading: boolean;
  filterState: FilterState;
  onFilterChange: (filterId: string, value: FilterValue) => void;
  showLabel?: boolean;
  tableId: TableFilterId;
  disabled?: boolean;
};

export function SearchFilter({
  filter,
  isLoading,
  filterState,
  onFilterChange,
  showLabel,
  tableId,
  disabled = false,
}: Props) {
  const filterValue = getFilterValue(filterState, filter.id, FilterType.Search);
  const [inputValue, setInputValue] = useState<string>(filterValue);

  // Keep local inputValue in sync with external filter value
  useEffect(() => {
    setInputValue(filterValue);
  }, [filterValue, filter.id]);

  const debouncedValue = useDebounce(inputValue, SEARCH_DEBOUNCE_DELAY_MS);

  // Call onFilterChange when debounced value changes
  useEffect(() => {
    if (debouncedValue !== filterValue) {
      onFilterChange(filter.id, debouncedValue);
    }
  }, [debouncedValue]);

  // Use filter-level showLabel if specified, otherwise fall back to prop
  const shouldShowLabel = filter.showLabel ?? showLabel;

  return (
    <SearchInput
      placeholder={m.common_search()}
      withBorder
      {...filter.props}
      disabled={Boolean(filter.props?.disabled || isLoading || disabled)}
      label={shouldShowLabel ? filter.label : undefined}
      name={`${tableId}-${filter.id}`}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        setInputValue(event.target.value)
      }
      value={inputValue}
    />
  );
}
